/**
 * Types + fetchery dla pluginu `c_crm_project_management` rozszerzajacego shell
 * `c_user_accounts` (projekty w ktorych uczestniczy zalogowany user).
 *
 * Backend (IRESTAPI, routing po NAZWIE METODY - `IAPI_` strip):
 *   - GET /IRESTAPI/v1/C_CRM_PROJECT_MANAGEMENT_CONTROLLER/userProjects
 *   - GET /IRESTAPI/v1/C_CRM_PROJECT_MANAGEMENT_CONTROLLER/userServices
 *   - GET /IRESTAPI/v1/C_CRM_PROJECT_MANAGEMENT_CONTROLLER/userWorkLog
 *
 * Zakres dostepu liczy backend (`IMODEL_M_CRM_PROJECTS::getUserProjects`): user
 * widzi projekt gdy jest jego wlascicielem, klientem albo czlonkiem zespolu.
 * Uslugi sa scope'owane TAK SAMO - przez uczestnictwo w projekcie do ktorego
 * usluga jest przypieta (`IMODEL_M_SERVICES__SERVICES::getUserServices`).
 *
 * Obie zakladki sa bramkowane dostepnoscia modulu po stronie backendu
 * (`sharingContentForApi`) - gdy modulu nie ma, akcja nie trafia do `extension.actions`,
 * wiec front nigdy nie wola endpointu ktory i tak by rzucil bledem.
 *
 * Lista jest client-side gridem (TanStack Table, ten sam wzorzec co faktury) -
 * backend zwraca caly bounded zbior, a sort/filtr/wyszukiwarka/paginacja dzieja
 * sie w react-cms.
 */

export type UserProject = {
  id: number;
  /** Numer projektu w formacie `P/{id}/{rok}`. */
  nr: string;
  name: string;
  description: string;
  /** Adres wdrozenia (kolumna `domain_online`), pusty string gdy nie ustawiony. */
  domain_online: string;
  /** Data utworzenia (Y-m-d H:i:s). */
  add_date: string;
  /** Daty planu (Y-m-d) - null gdy nie ustawione. */
  start_date: string | null;
  end_date: string | null;
  deadline_date: string | null;
  /** Numer etapu (slownik `crm_project_stages`). */
  stage: number;
  /** Etykieta etapu ze slownika; null gdy slownik nie zawiera pozycji. */
  stage_name: string | null;
  /** Rola usera w zespole; null gdy dostep jako wlasciciel albo klient. */
  role_name: string | null;
  /** Czy user jest klientem tego projektu. */
  is_client: boolean;
  ended: boolean;
  received: boolean;
  /** Powod wstrzymania projektu; null gdy projekt nie jest wstrzymany. */
  stop_reason: string | null;
};

export type UserProjectsResponse = {
  ok: true;
  data: {
    projects: UserProject[];
    /** `true` gdy lista zostala obcieta do limitu serwera (300). */
    truncated: boolean;
  };
};

/**
 * Pobiera liste projektow usera z route handler proxy.
 *
 * @returns `{ projects, truncated }`
 * @throws Error gdy fetch fail / response !ok (caller obsluguje przez try/catch).
 */
export async function fetchUserProjects(): Promise<{
  projects: UserProject[];
  truncated: boolean;
}> {
  const res = await fetch("/api/iiapi/c-crm-project-management/projects", {
    cache: "no-store",
  });
  if (!res.ok) {
    throw new Error(`Fetch projects failed: HTTP ${res.status}`);
  }
  const body = (await res.json()) as
    | UserProjectsResponse
    | { ok: false; error?: string; message?: string };
  if (!("ok" in body) || body.ok !== true) {
    throw new Error(
      "message" in body ? body.message ?? "Fetch failed" : "Invalid response shape",
    );
  }
  return {
    projects: body.data?.projects ?? [],
    truncated: body.data?.truncated ?? false,
  };
}

export type UserService = {
  id: number;
  /** Numer uslugi w formacie `S/{id}`. */
  nr: string;
  id_project: number;
  /** Nazwa projektu do ktorego usluga jest przypieta. */
  project_name: string | null;
  type: number;
  /** Nazwa typu uslugi ze slownika modulu Uslugi; null gdy typ usuniety. */
  type_name: string | null;
  package: number;
  package_name: string | null;
  id_status: number;
  status_name: string | null;
  description: string | null;
  /** Data uruchomienia (Y-m-d H:i:s). */
  add_date: string;
  /** Data wygasniecia (Y-m-d H:i:s); null dla uslug bezterminowych. */
  expiry_date: string | null;
  /**
   * Czy usluga jest BIEZACA - status w zbiorze {nowa, w trakcie dostarczania,
   * do aktywacji, aktywna, zablokowana}. To domyslny widok listy, identycznie jak
   * w gridzie modulu Uslugi w panelu admina (`getActiveServiceStatuses`).
   * Pozostale statusy = uslugi nieaktywne (historia).
   * NIE to samo co `id_status === 4` - zablokowana tez jest biezaca.
   */
  is_current: boolean;
  /**
   * Dni do wygasniecia; wartosc UJEMNA = tyle dni PO terminie.
   * null dla uslug bezterminowych (brak `expiry_date`) - to co innego niz 0.
   */
  expiry_days: number | null;
  /** Czy `expiry_date` juz minela (niezaleznie od statusu). */
  is_expired: boolean;
  /**
   * Ostatni dzien okresu karencji (Y-m-d) - po nim usluga jest ostatecznie wygasla
   * i podlega usunieciu. null dla uslug bezterminowych.
   */
  grace_until: string | null;
  /** Czy okres karencji juz minal (usluga do usuniecia). */
  is_after_grace: boolean;
  /** Czy zlozono rezygnacje z uslugi. */
  resignation: boolean;
  /** Data rezygnacji (Y-m-d); null gdy nie zlozono. */
  resignation_date: string | null;
  /**
   * Absolutny URL panelu przedluzenia w domenie backendu
   * (`M_SERVICES_MCONTROLLER::getRenewPanelUrl` - id uslugi zaszyfrowane w adresie,
   * klient nie musi sie logowac). null gdy przedluzenie nie jest teraz mozliwe
   * wg dat i konfiguracji pakietu.
   */
  renew_href: string | null;
};

export type UserServicesResponse = {
  ok: true;
  data: {
    services: UserService[];
    /** `true` gdy lista zostala obcieta do limitu serwera (500). */
    truncated: boolean;
  };
};

/**
 * Pobiera liste uslug z projektow usera z route handler proxy.
 *
 * @returns `{ services, truncated }`
 * @throws Error gdy fetch fail / response !ok (caller obsluguje przez try/catch).
 */
export async function fetchUserServices(returnUrl?: string): Promise<{
  services: UserService[];
  truncated: boolean;
}> {
  /* `returnUrl` - adres, na ktory panel przedluzenia odesle klienta po zakonczeniu.
     Podaje go przegladarka, bo backend obsluguje wiele domen CMS i nie wie, z ktorej
     klient przyszedl. Trafia do zaszyfrowanego payloadu linku po stronie PHP. */
  const query = returnUrl ? `?return_url=${encodeURIComponent(returnUrl)}` : "";
  const res = await fetch(`/api/iiapi/c-crm-project-management/services${query}`, {
    cache: "no-store",
  });
  if (!res.ok) {
    throw new Error(`Fetch services failed: HTTP ${res.status}`);
  }
  const body = (await res.json()) as
    | UserServicesResponse
    | { ok: false; error?: string; message?: string };
  if (!("ok" in body) || body.ok !== true) {
    throw new Error(
      "message" in body ? body.message ?? "Fetch failed" : "Invalid response shape",
    );
  }
  return {
    services: body.data?.services ?? [],
    truncated: body.data?.truncated ?? false,
  };
}

/* -------------------------------------------------------------------------
 * Dziennik pracy
 *
 * Backend (IRESTAPI):
 *   - GET /IRESTAPI/v1/C_CRM_PROJECT_MANAGEMENT_CONTROLLER/userWorkLog
 *
 * Dane widzi WYLACZNIE kontrahent projektu. Projekty, w ktorych user jest czlonkiem
 * zespolu albo wlascicielem, wracaja w `restricted_projects` z zamaskowanym adresem
 * konta kontrahenta - front pokazuje wtedy informacje, na ktore konto sie zalogowac.
 * Backend pokazuje prace z dnia D od 12:00 najblizszego dnia roboczego po D (kalendarz
 * dni wolnych), ukrywa wpisy z zerowym czasem sprzedanym oraz zwraca wylacznie czas
 * sprzedany klientowi.
 * ------------------------------------------------------------------------- */

/** Blad odczytu niosacy kod Imperiala - widok moze na niego zareagowac inaczej niz na awarie. */
export class TaskApiError extends Error {
  readonly code: number;

  constructor(message: string, code: number) {
    super(message);
    this.name = "TaskApiError";
    this.code = code;
  }
}

/**
 * Wspolne odczytanie odpowiedzi proxy - rzuca `TaskApiError` z kodem Imperiala,
 * zeby wywolujacy mogl rozpoznac konkretny powod (np. niedostepny projekt) zamiast
 * pokazywac ogolne "cos poszlo nie tak".
 */
async function readTaskJson<T>(res: Response, fallbackMessage: string): Promise<T> {
  const body = (await res.json().catch(() => null)) as
    | { ok: true; data: T }
    | { ok: false; error?: string; message?: string; code?: number }
    | null;

  if (!body || !("ok" in body)) {
    throw new TaskApiError(fallbackMessage, 0);
  }
  if (body.ok !== true) {
    throw new TaskApiError(body.message ?? fallbackMessage, body.code ?? 0);
  }
  return body.data;
}

/** Pojedynczy wpis dziennika pracy. */
export type WorkLogEntry = {
  id: number;
  /** Dzien pracy (Y-m-d). */
  work_date: string;
  project_id: number;
  /** Numer projektu w formacie `P/{id}/{rok}`. */
  project_nr: string;
  project_name: string;
  task_id: number | null;
  task_name: string | null;
  /** Pracownik zamaskowany: imie i pierwsza litera nazwiska (`Jan K***`); null gdy brak imienia. */
  employee: string | null;
  /** Rodzaj pracy ze slownika aktywnosci; null gdy brak etykiety. */
  activity_name: string | null;
  text: string;
  /** Czas sprzedany klientowi w godzinach. */
  hours: number;
  /** `false` = godziny przepracowane, ale nierozliczane z klientem. */
  billed: boolean;
  /** Powod nierozliczenia (tylko przy `billed=false`); null gdy nie podano. */
  not_billed_reason: string | null;
};

/** Suma godzin nierozliczanych dla jednego powodu. */
export type WorkLogNotBilledReason = {
  /** Powod; null = wpisy bez podanego powodu. */
  reason: string | null;
  hours: number;
  positions: number;
};

/** Projekt, w ktorym user nie jest kontrahentem - dane dziennika dostepne na innym koncie. */
export type WorkLogRestrictedProject = {
  id: number;
  nr: string;
  name: string;
  /**
   * Zamaskowany adres e-mail konta kontrahenta, a gdy konto nie ma adresu - zamaskowany
   * login. null gdy projekt nie ma kontrahenta.
   */
  client_account_masked: string | null;
};

export type WorkLog = {
  /** Zakres faktycznie uzyty przez backend (Y-m-d) - domyslnie biezacy miesiac. */
  date_from: string;
  date_to: string;
  /** Ostatni dzien, z ktorego wpisy sa juz widoczne (liczony z kalendarza dni roboczych). */
  visible_until: string;
  /** Projekty kontrahenta - opcje filtra. */
  projects: Array<{ id: number; nr: string; name: string }>;
  restricted_projects: WorkLogRestrictedProject[];
  entries: WorkLogEntry[];
  /** Sumy po CALYM zakresie, niezaleznie od obciecia listy. */
  summary: {
    billed_hours: number;
    not_billed_hours: number;
    total_hours: number;
    not_billed_reasons: WorkLogNotBilledReason[];
  };
  /** `true` gdy lista wpisow zostala obcieta do limitu serwera (1000). */
  truncated: boolean;
};

/**
 * Pobiera dziennik pracy zalogowanego kontrahenta.
 *
 * @param filter Zakres dat (Y-m-d) i opcjonalny projekt; brak dat = biezacy miesiac.
 * @throws TaskApiError z kodem Imperiala (2260023 = projekt niedostepny, 2260062 = zla data).
 */
export async function fetchWorkLog(filter: {
  dateFrom?: string;
  dateTo?: string;
  projectId?: number | null;
}): Promise<WorkLog> {
  const query = new URLSearchParams();
  if (filter.dateFrom) query.set("date_from", filter.dateFrom);
  if (filter.dateTo) query.set("date_to", filter.dateTo);
  if (filter.projectId) query.set("id_project", String(filter.projectId));

  const res = await fetch(
    `/api/iiapi/c-crm-project-management/worklog${query.toString() ? `?${query.toString()}` : ""}`,
    { cache: "no-store" },
  );
  return readTaskJson<WorkLog>(res, "Nie udało się pobrać dziennika pracy.");
}
